import React from "react";
import { Flag, Sparkles } from "lucide-react";
import { SQUAD_PRIORITIES, SQUAD_STATUSES, priorityCounts } from "./squadStats";
import { formatDate } from "./shared";

const PRIORITY_RANK = Object.fromEntries(SQUAD_PRIORITIES.map((p, i) => [p.value, i]));
const priorityOf = (value) => SQUAD_PRIORITIES.find((p) => p.value === value);
const statusOf = (value) => SQUAD_STATUSES.find((s) => s.value === value);
const label = "uppercase text-faint";

const sentence = ({ name, stats, urgentHigh, scopeText }) => {
  if (!stats.tasks) return "No tasks in this scope.";
  const parts = [`${stats.done} done`, `${stats.open} open`];
  if (stats.overdue) parts.push(`${stats.overdue} overdue`);
  const tail = urgentHigh ? `${urgentHigh} of them ${urgentHigh === 1 ? "is" : "are"} Urgent or High priority.` : "No Urgent or High priority work.";
  return `${name} has ${stats.tasks} ${stats.tasks === 1 ? "task" : "tasks"} ${scopeText}: ${parts.join(", ")}. ${tail}`;
};

/**
 * Per-member summary shown when a members-table row is expanded: a sentence,
 * priority and status breakdowns for the selected scope, and (when the scope
 * includes the current sprint) the member's open tasks there.
 */
const MemberSummary = ({ member, stats, scopeTasks, scopeText, openTasks, onOpenTask }) => {
  const priorities = priorityCounts(scopeTasks);
  const urgentHigh = priorities.critical + priorities.high;
  const name = (member.fullNameEn || "").split(" ")[0] || "This member";
  const sorted = openTasks
    ? [...openTasks].sort(
        (a, b) =>
          (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9) ||
          (a.dueDate ? new Date(a.dueDate) : Infinity) - (b.dueDate ? new Date(b.dueDate) : Infinity),
      )
    : [];
  const today = new Date().setHours(0, 0, 0, 0);

  return (
    <>
      <div className="flex flex-col" style={{ background: "var(--grad-soft)", border: "1px solid var(--accent-line)", borderRadius: 14, padding: "14px 16px", gap: 14 }}>
        <p className="flex items-start gap-2.5 text-[13.5px] text-ink" style={{ lineHeight: 1.55, textWrap: "pretty" }}>
          <Sparkles className="shrink-0 mt-0.5" style={{ width: 16, height: 16, color: "var(--accent)" }} />
          {sentence({ name, stats, urgentHigh, scopeText })}
        </p>
        {stats.tasks > 0 && (
          <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
            <div className="min-w-0">
              <p className={label} style={{ fontSize: 11.5, letterSpacing: ".06em" }}>Priority</p>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {SQUAD_PRIORITIES.map((p) => (
                  <span
                    key={p.value}
                    className="inline-flex items-center gap-1.5 text-[12px] text-muted"
                    style={{ padding: "3px 9px", borderRadius: 999, background: `${p.color}24`, opacity: priorities[p.value] ? 1 : 0.45 }}
                  >
                    <i style={{ width: 6, height: 6, borderRadius: 999, background: p.color }} />
                    {p.label} <b className="text-ink tabular-nums">{priorities[p.value]}</b>
                  </span>
                ))}
              </div>
            </div>
            <div className="min-w-0">
              <p className={label} style={{ fontSize: 11.5, letterSpacing: ".06em" }}>Status</p>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {SQUAD_STATUSES.filter((s) => stats.byStatus[s.value] > 0).map((s) => (
                  <span
                    key={s.value}
                    className="inline-flex items-center gap-1.5 text-[12px] text-muted"
                    style={{ padding: "3px 9px", borderRadius: 999, background: "var(--panel)", border: "1px solid var(--line)" }}
                  >
                    <i style={{ width: 6, height: 6, borderRadius: 2, background: s.color }} />
                    {s.label} <b className="text-ink tabular-nums">{stats.byStatus[s.value]}</b>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {openTasks && sorted.length > 0 && (
        <div className="min-w-0">
          <p className={`${label} mb-2`} style={{ fontSize: 11.5, letterSpacing: ".06em" }}>Open in the current sprint · {sorted.length}</p>
          <div style={{ border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden" }}>
            {sorted.map((t, i) => {
              const pr = priorityOf(t.priority);
              const st = statusOf(t.status);
              const late = t.dueDate && new Date(t.dueDate).setHours(0, 0, 0, 0) < today;
              const date = t.dueDate ? formatDate(t.dueDate, { day: "numeric", month: "short" }) : null;
              return (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => onOpenTask(t)}
                  className="grid w-full items-center text-left text-[13px] hover:bg-hover"
                  style={{ gridTemplateColumns: "56px minmax(0,1fr) 92px 118px 130px", gap: 12, padding: "9px 12px", borderBottom: i < sorted.length - 1 ? "1px solid var(--line)" : "none" }}
                >
                  <span className="font-mono text-faint" style={{ fontSize: 11.5 }}>#{t.number}</span>
                  <span className="font-medium text-ink truncate" title={t.title}>{t.title}</span>
                  <span className="inline-flex items-center gap-1.5 text-muted">
                    {pr && <Flag style={{ width: 12, height: 12, color: pr.color, fill: pr.color }} />}
                    {pr?.label || "—"}
                  </span>
                  <span>
                    {st && (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap" style={{ padding: "2px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: `${st.color}26`, color: st.color }}>
                        <i style={{ width: 5, height: 5, borderRadius: 999, background: st.color }} />
                        {st.label}
                      </span>
                    )}
                  </span>
                  <span className="text-right whitespace-nowrap" style={{ fontSize: 12, color: late ? "var(--red)" : "var(--faint)" }}>
                    {date ? (late ? `Overdue · ${date}` : `Due ${date}`) : "No deadline"}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default MemberSummary;
